import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useRealtimeNotifications } from "@/hooks/dashboard/useRealtimeNotifications";
import { Activity } from "lucide-react";

interface NotificationStatusChartProps {
  timeRange: string;
  refreshInterval: number;
}

const COLORS = {
  'open': 'hsl(var(--destructive))',
  'in_behandeling': 'hsl(var(--warning))',
  'gesloten': 'hsl(var(--success))',
  'opgelost': 'hsl(var(--success))',
  'geannuleerd': 'hsl(var(--muted))'
};

const STATUS_LABELS = {
  'open': 'Open',
  'in_behandeling': 'In Behandeling',
  'gesloten': 'Gesloten',
  'opgelost': 'Opgelost',
  'geannuleerd': 'Geannuleerd'
};

const NotificationStatusChart: React.FC<NotificationStatusChartProps> = ({
  timeRange,
  refreshInterval
}) => {
  const { statusStats, isLoading } = useRealtimeNotifications(timeRange, refreshInterval);

  const chartData = statusStats.map(stat => ({
    name: STATUS_LABELS[stat.status as keyof typeof STATUS_LABELS] || stat.status,
    value: stat.count,
    status: stat.status,
    fill: COLORS[stat.status as keyof typeof COLORS] || 'hsl(var(--muted))'
  }));

  const chartConfig = {
    value: {
      label: "Aantal",
    },
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Melding Status</CardTitle>
            <CardDescription>Real-time status verdeling</CardDescription>
          </div>
          <Activity className="h-4 w-4 text-muted-foreground animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Melding Status</CardTitle>
          <CardDescription>Real-time status verdeling</CardDescription>
        </div>
        <Activity className="h-4 w-4 text-green-500 animate-pulse" />
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={120}
                paddingAngle={5}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <ChartTooltip content={<ChartTooltipContent />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>
        
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-4">
          {chartData.map((item) => (
            <div key={item.status} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: item.fill }}
              ></div>
              <span className="text-sm">{item.name}: {item.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationStatusChart;